import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import countries from "i18n-iso-countries";
import fr from "i18n-iso-countries/langs/fr.json";
import { FiArrowLeft, FiMail, FiMessageCircle, FiPhone } from "react-icons/fi";
import {
  EmptyState,
  StatusBadge,
} from "../../components/dashboard/DashboardUI";
import RequestDetail from "../../components/modals/RequestDetail";
import Loader from "../../components/loader/Loader";
import {
  createFollowUp,
  getFollowUps,
} from "../../redux/actions/followUpActions";
import { requestTitle } from "../../utils/requestDisplay";
import {
  FOLLOW_UP_CHANNELS,
  clientName,
  clientOf,
  emailLink,
  phoneLink,
  reminderMessage,
  whatsappLink,
} from "../../utils/salesContact";

countries.registerLocale(fr);

const EMPTY_FORM = { channel: "", notes: "" };

const toast = (icon, title, text) =>
  Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 4000,
  }).fire({ icon, title, text });

const agentName = (agent) =>
  agent && typeof agent === "object"
    ? [agent.firstname, agent.lastname].filter(Boolean).join(" ")
    : "";

// Reminders of a client who did not finish a request
function FollowUpDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { procedure } = location.state || {};
  const { user } = useSelector((state) => state.authUser);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [followUps, setFollowUps] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showRequest, setShowRequest] = useState(false);

  const load = () =>
    Promise.resolve(
      dispatch(
        getFollowUps(user.token, procedure.reference, (data) =>
          setFollowUps(Array.isArray(data) ? data : []),
        ),
      ),
    );

  useEffect(() => {
    if (!procedure?.reference || !user?.token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procedure?.reference, user?.token]);

  if (!procedure) {
    return (
      <EmptyState title="Démarche introuvable">
        Revenez à la liste des démarches inachevées.
      </EmptyState>
    );
  }

  const client = clientOf(procedure);
  const message = reminderMessage(procedure, user);
  const contacts = [
    {
      label: "Appeler",
      icon: FiPhone,
      href: phoneLink(client.mobile),
      channel: "phone",
    },
    {
      label: "WhatsApp",
      icon: FiMessageCircle,
      href: whatsappLink(client.whatsApp || client.mobile, message),
      channel: "whatsapp",
    },
    {
      label: "E-mail",
      icon: FiMail,
      href: emailLink(client.email, procedure, message),
      channel: "email",
    },
  ].filter((contact) => contact.href);

  const history = [...followUps].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );

  const handleCreated = ({ success, message: text }) => {
    setSubmitting(false);
    if (!success) {
      toast("error", "Échec", text);
      return;
    }
    setForm(EMPTY_FORM);
    toast("success", "Relance enregistrée", text);
    load();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitting(true);
    dispatch(
      createFollowUp(
        user.token,
        {
          request: procedure.reference,
          channel: form.channel,
          notes: form.notes.trim(),
        },
        handleCreated,
      ),
    );
  };

  return (
    <>
      <Loader isLoading={submitting} />
      <div className="dash-page-header">
        <div>
          <button
            type="button"
            className="dash-back"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft aria-hidden="true" /> Démarches inachevées
          </button>
          <h1 className="dash-page-title">{clientName(procedure)}</h1>
          <p className="dash-page-lead">
            {requestTitle(procedure)} · #{procedure.reference} · commencée le{" "}
            {moment(procedure.createdAt).format("DD/MM/YYYY")}
          </p>
        </div>
        <div className="dash-header-actions">
          <StatusBadge status={procedure.status} />
          <button
            type="button"
            className="dash-button is-secondary"
            onClick={() => setShowRequest(true)}
          >
            Voir la démarche
          </button>
        </div>
      </div>

      <div className="dash-detail-grid">
        <section className="dash-card">
          <h2 className="dash-card-title">Nouvelle relance</h2>
          <form className="dash-form" onSubmit={handleSubmit}>
            <label htmlFor="follow-up-channel">Canal utilisé</label>
            <select
              id="follow-up-channel"
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })}
              required
            >
              <option value="">Choisissez le canal</option>
              {Object.entries(FOLLOW_UP_CHANNELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            <label htmlFor="follow-up-notes">Compte rendu</label>
            <textarea
              id="follow-up-notes"
              rows={4}
              minLength={5}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Ce que le client a répondu, prochaine étape…"
              required
            />

            <div className="dash-form-actions">
              <button
                type="submit"
                className="dash-button"
                disabled={submitting}
              >
                Enregistrer la relance
              </button>
            </div>
          </form>

          <h2 className="dash-card-title dash-section-gap">
            Historique des relances
          </h2>
          {loading ? (
            <p className="dash-loading">Chargement…</p>
          ) : history.length === 0 ? (
            <p className="dash-page-lead">
              Ce client n'a pas encore été relancé.
            </p>
          ) : (
            <ul className="dash-history">
              {history.map((item) => (
                <li key={item._id}>
                  <div className="dash-history-head">
                    <strong>
                      {FOLLOW_UP_CHANNELS[item.channel] || item.channel}
                    </strong>
                    <span className="dash-cell-sub">
                      {moment(item.createdAt).format("DD/MM/YYYY à HH:mm")}
                      {agentName(item.agent) && ` · ${agentName(item.agent)}`}
                    </span>
                  </div>
                  <p className="dash-pre">{item.notes}</p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <aside className="dash-card">
          <h2 className="dash-card-title">Contacter le client</h2>
          {contacts.length ? (
            <div className="dash-contact-actions">
              {contacts.map(({ label, icon: Icon, href }) => (
                <a
                  key={label}
                  className="dash-button is-secondary"
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                >
                  <Icon aria-hidden="true" /> {label}
                </a>
              ))}
            </div>
          ) : (
            <p className="dash-page-lead">
              Aucun moyen de contact renseigné.
            </p>
          )}
          <p className="dash-cell-sub dash-section-gap">
            WhatsApp et l'e-mail s'ouvrent avec un message de relance déjà
            rédigé, à adapter avant l'envoi.
          </p>

          <dl className="dash-details is-stacked dash-section-gap">
            <dt>E-mail</dt>
            <dd>{client.email || "—"}</dd>
            <dt>Téléphone</dt>
            <dd>{client.mobile || "—"}</dd>
            {client.whatsApp && (
              <>
                <dt>WhatsApp</dt>
                <dd>{client.whatsApp}</dd>
              </>
            )}
            {client.country && (
              <>
                <dt>Pays de résidence</dt>
                <dd>{countries.getName(client.country, "fr") || client.country}</dd>
              </>
            )}
            {client.profession && (
              <>
                <dt>Profession</dt>
                <dd>{client.profession}</dd>
              </>
            )}
          </dl>
        </aside>
      </div>

      {showRequest && (
        <RequestDetail
          request={procedure}
          onHide={() => setShowRequest(false)}
        />
      )}
    </>
  );
}

export default FollowUpDetails;
